import { useEffect, useState } from 'react';
import './UndoBanner.css';
import type { GMDispatchSnapshot } from '../../hooks/useGMDispatcher';

export interface UndoBannerProps {
  snapshot: GMDispatchSnapshot | null;
  windowMs?: number;
  onUndo: (snapshot: GMDispatchSnapshot) => void;
  onExpire: () => void;
}

/**
 * V3 Voice GM (2026-06-02) — 3s undo banner.
 *
 * Appears as a fixed banner at the top of the runner when the
 * GMOrchestrator dispatches an action with state mutation. Shows the
 * dispatched action label + an Undo button. Auto-dismisses after
 * `windowMs` (default 3000ms). Click Undo → reverts the state +
 * dismisses immediately.
 *
 * Per V3 Q4 user pick: "3s undo banner — auto-dispatches but shows
 * Undo for 3s". The banner is the affordance.
 */
export function UndoBanner({
  snapshot,
  windowMs = 3000,
  onUndo,
  onExpire,
}: UndoBannerProps) {
  const [remaining, setRemaining] = useState<number>(windowMs);

  useEffect(() => {
    if (snapshot === null) {
      setRemaining(windowMs);
      return;
    }
    setRemaining(windowMs);
    const start =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : 0;
    const interval = setInterval(() => {
      const elapsed =
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now() - start
          : 0;
      const left = Math.max(0, windowMs - elapsed);
      setRemaining(left);
      if (left <= 0) {
        clearInterval(interval);
        onExpire();
      }
    }, 100);
    return () => clearInterval(interval);
  }, [snapshot, windowMs, onExpire]);

  if (snapshot === null) {
    return null;
  }
  const action = snapshot.action;
  const tool = action?.tool ?? null;
  const label =
    tool === 'pick_decision'
      ? `Dispatched: ${String(action?.args.label ?? '')}`
      : tool === 'roll_skill'
        ? `Rolling: ${String(action?.args.skill_name ?? '')} EF${String(action?.args.ease_factor ?? '5')}`
        : tool !== null
          ? `GM action: ${tool}`
          : 'GM replied';

  const secondsLeft = Math.ceil(remaining / 1000);

  return (
    <div
      className="br-undo-banner"
      data-testid="br-undo-banner"
      role="status"
      aria-live="polite"
    >
      <span className="br-undo-banner-label br-label">{label}</span>
      <span className="br-undo-banner-timer br-label" aria-hidden="true">
        {secondsLeft}s
      </span>
      <button
        type="button"
        className="br-undo-banner-button"
        data-testid="br-undo-banner-button"
        onClick={() => onUndo(snapshot)}
      >
        Undo
      </button>
    </div>
  );
}

export default UndoBanner;
